/**
 * Loads the bundle once and hands it to the whole app.
 *
 * Everything downstream — every page, every measure — runs off this single in-memory
 * dataset. That is what keeps bar-click cross-filtering instant: no round trip, the
 * measures recompute in the browser.
 */
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { Dataset } from './types'
import { loadDataset } from './client'
import { useAuth } from '@/auth/AuthProvider'
import { applyAgedNoTouch } from './agedNoTouch'
import { AgeLimitsCtx, useFetchAgeLimits } from './payerTypeSettings'
import { PageLimitCtx } from '@/access/pageLimit'

interface DatasetState {
  /**
   * The bundle with each payer type's 0% touch age limit applied (data/agedNoTouch.ts):
   * the one dataset every page reads, so every figure moves together.
   */
  dataset: Dataset | null
  loading: boolean
  error: string | null
  fromCache: boolean
  reload: () => void
}

const Ctx = createContext<DatasetState | null>(null)

export function useDataset(): DatasetState {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useDataset must be used inside <DatasetProvider>')
  return ctx
}

/** Convenience for pages that cannot render without data. */
export function useLoadedDataset(): Dataset {
  const { dataset } = useDataset()
  if (!dataset) throw new Error('Dataset not loaded — render inside <RequireDataset>')
  return dataset
}

export function DatasetProvider({ children }: { children: ReactNode }) {
  const { token, ready } = useAuth()
  const [dataset, setDataset] = useState<Dataset | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fromCache, setFromCache] = useState(false)
  const [nonce, setNonce] = useState(0)
  const ageLimits = useFetchAgeLimits()
  const applied = useMemo(
    () => (dataset ? applyAgedNoTouch(dataset, ageLimits.limits) : null),
    [dataset, ageLimits.limits],
  )

  useEffect(() => {
    if (!ready) return
    let cancelled = false
    setLoading(true)
    setError(null)
    void loadDataset(token)
      .then((r) => {
        if (cancelled) return
        setDataset(r.dataset)
        setFromCache(r.fromCache)
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [token, ready, nonce])

  return (
    <Ctx.Provider
      value={{ dataset: applied, loading, error, fromCache, reload: () => setNonce((n) => n + 1) }}
    >
      <AgeLimitsCtx.Provider value={ageLimits}>
        <PageLimitCtx.Provider value={dataset?.pages ?? null}>{children}</PageLimitCtx.Provider>
      </AgeLimitsCtx.Provider>
    </Ctx.Provider>
  )
}
